import React, { forwardRef } from 'react';
import { AlertCircle } from 'lucide-react';

interface TextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
  hint?: string;
  fullWidth?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(({
  label,
  error,
  hint,
  fullWidth = true,
  className = '',
  id,
  ...props
}, ref) => {
  const textareaId = id || label?.toLowerCase().replace(/\s/g, '-');
  
  return (
    <div className={fullWidth ? 'w-full' : ''}>
      {label && (
        <label 
          htmlFor={textareaId} 
          className="block text-sm font-medium text-secondary-700 mb-1.5"
        >
          {label}
        </label>
      )}
      <div className="relative">
        <textarea
          ref={ref}
          id={textareaId}
          className={`
            w-full px-4 py-2.5 bg-white border rounded-lg text-secondary-900 
            placeholder:text-secondary-400 transition-all duration-200
            focus:outline-none focus:ring-2 focus:ring-offset-0 resize-y min-h-[100px]
            disabled:bg-secondary-50 disabled:text-secondary-500 disabled:cursor-not-allowed
            ${error 
              ? 'border-danger-300 focus:border-danger-500 focus:ring-danger-100' 
              : 'border-secondary-200 focus:border-primary-500 focus:ring-primary-100'
            }
            ${className}
          `}
          {...props}
        />
        {error && (
          <div className="absolute right-3 top-3 text-danger-500">
            <AlertCircle className="w-4 h-4" />
          </div>
        )}
      </div>
      {error && (
        <p className="mt-1.5 text-sm text-danger-600">{error}</p>
      )}
      {hint && !error && (
        <p className="mt-1.5 text-sm text-secondary-500">{hint}</p>
      )}
    </div>
  );
});

Textarea.displayName = 'Textarea';
